// On-Track Technologies — group rows into a folder-style tree.
//
// The client's structures group work by one field after another and indent
// what falls under each, and they want that on the pages that replaced those
// structures. This turns a flat list into the same shape, without a folder:
//
//   buildTree(items, { groupBy, childrenOf, rowOf })
//     groupBy    [(it) => string | null]  one function per grouping LEVEL
//     childrenOf (it) => items[]          what hangs under a row (optional)
//     rowOf      (it, level) => row       how an item becomes a row
//
// Returns FLAT rows carrying `level`, `isGroup`, `count` and, on a group,
// the span of everything under it — flat because every renderer here (the
// table, the Gantt) wants to walk rows in order and indent by level, and a
// nested shape makes both of them harder for no gain.

export function buildTree(items, { groupBy = [], childrenOf, rowOf, collapsed } = {}) {
  const out = [];
  const isCollapsed = (path) => !!(collapsed && collapsed.has(path));

  const walk = (list, level, depth, parentPath) => {
    if (depth >= groupBy.length) {
      list.forEach((it) => {
        const row = rowOf(it, level);
        out.push(row);
        const kids = childrenOf ? childrenOf(it) : null;
        if (kids && kids.length && !isCollapsed(row.path || row.key)) {
          kids.forEach((k) => out.push({ ...rowOf(k, level + 1), isChild: true }));
        }
      });
      return;
    }
    const by = new Map();
    list.forEach((it) => {
      const k = groupBy[depth](it);
      const key = k == null || k === '' ? '(none)' : String(k);
      if (!by.has(key)) by.set(key, []);
      by.get(key).push(it);
    });
    [...by.entries()].sort((a, b) => a[0].localeCompare(b[0])).forEach(([name, group]) => {
      const path = `${parentPath}/${name}`;
      const dates = group.flatMap((it) => {
        const r = rowOf(it, level);
        return [r.start, r.due].filter(Boolean);
      }).sort();
      out.push({
        key: path, path, summary: name, isGroup: true, level, count: group.length,
        start: dates[0] || '', due: dates[dates.length - 1] || '', items: group,
      });
      if (!isCollapsed(path)) walk(group, level + 1, depth + 1, path);
    });
  };

  walk(items, 0, 0, '');
  return out;
}
